import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["receptorType", "button", "patientCuit", "responsibleCuit"]
  static values = {
    appointmentId: String,
    availableTypes: Array,
    isDevelopment: Boolean,
    doctorType: String
  }

  connect() {
    this.updateButtonState()
    this.updateInvoiceInfo()

    // Agregar listeners para los campos de descripción
    this.addDescriptionFieldListeners()
  }

  addDescriptionFieldListeners() {
    this._baseDescriptionField = this.element.querySelector('textarea[name="appointment[base_description]"]')
    this._diagnosisHealthInsuranceField = this.element.querySelector('textarea[name="appointment[diagnosis_health_insurance]"]')
    this._onDescChanged = () => this.updateButtonState()

    if (this._baseDescriptionField) {
      this._baseDescriptionField.addEventListener('input', this._onDescChanged)
      this._baseDescriptionField.addEventListener('blur', this._onDescChanged)
    }

    if (this._diagnosisHealthInsuranceField) {
      this._diagnosisHealthInsuranceField.addEventListener('input', this._onDescChanged)
      this._diagnosisHealthInsuranceField.addEventListener('blur', this._onDescChanged)
    }
  }

  disconnect() {
    if (this._baseDescriptionField && this._onDescChanged) {
      this._baseDescriptionField.removeEventListener('input', this._onDescChanged)
      this._baseDescriptionField.removeEventListener('blur', this._onDescChanged)
    }
    if (this._diagnosisHealthInsuranceField && this._onDescChanged) {
      this._diagnosisHealthInsuranceField.removeEventListener('input', this._onDescChanged)
      this._diagnosisHealthInsuranceField.removeEventListener('blur', this._onDescChanged)
    }
  }

  receptorTypeChanged() {
    this.updateButtonState()
    this.updateInvoiceInfo()
  }

  updateButtonState() {
    const selectedReceptorType = this.hasReceptorTypeTarget ? this.receptorTypeTarget.value : null

    // Si no hay selección de tipo de receptor, deshabilitar el botón
    if (!selectedReceptorType) {
      this.buttonTarget.disabled = true
      return
    }

    // Verificar si se puede determinar el tipo de factura
    const doctorType = this.doctorTypeValue
    const invoiceDetails = this.determineInvoiceDetails(doctorType, selectedReceptorType)

    if (!invoiceDetails) {
      // No se puede determinar el tipo de factura, deshabilitar el botón
      this.buttonTarget.disabled = true
      return
    }

    // Verificar si se cumplen los requisitos de CUIT y razón social
    const hasValidData = this.checkDataRequirements(invoiceDetails, selectedReceptorType)

    // Habilitar el botón solo si hay selección, se puede determinar el tipo de factura y se cumplen los requisitos
    this.buttonTarget.disabled = !hasValidData
  }

  checkDataRequirements(invoiceDetails, receptorType) {
    // Verificar que la descripción de factura esté completa
    const baseDescriptionField = document.querySelector('textarea[name="appointment[base_description]"]')
    const diagnosisHealthInsuranceField = document.querySelector('textarea[name="appointment[diagnosis_health_insurance]"]')

    if (!baseDescriptionField || baseDescriptionField.value.trim() === '') {
      return false
    }

    if (!diagnosisHealthInsuranceField || diagnosisHealthInsuranceField.value.trim() === '') {
      return false
    }

    // Verificar CUIT si es requerido
    if (invoiceDetails.requiresCuit) {
      const patientCuit = this.patientCuitTarget ? this.patientCuitTarget.textContent.trim() : ''
      const responsibleCuit = this.responsibleCuitTarget ? this.responsibleCuitTarget.textContent.trim() : ''

      if (!patientCuit && !responsibleCuit) {
        return false
      }
    }

    // Verificar razón social si es requerida
    if (invoiceDetails.requiresBusinessName) {
      const patientCompanyName = document.querySelector('[data-patient-company-name]')?.textContent?.trim() || ''
      const responsibleCompanyName = document.querySelector('[data-responsible-company-name]')?.textContent?.trim() || ''

      // Priorizar paciente primero, sino usar responsable
      if (!patientCompanyName && !responsibleCompanyName) {
        return false
      }
    }

    return true
  }

  updateInvoiceInfo() {
    const selectedReceptorType = this.hasReceptorTypeTarget ? this.receptorTypeTarget.value : null
    const doctorType = this.doctorTypeValue

    if (!selectedReceptorType) {
      this.hideInvoiceInfo()
      return
    }

    const invoiceDetails = this.determineInvoiceDetails(doctorType, selectedReceptorType)
    if (invoiceDetails) {
      this.showInvoiceInfo(invoiceDetails)
    } else {
      this.hideInvoiceInfo()
    }
  }

  determineInvoiceDetails(doctorType, receptorType) {
    // Lógica para determinar tipo de factura según doctor y receptor
    if (doctorType === 'monotributista' || doctorType === 'responsable_exento') {
      // Monotributista y Responsable Exento siempre emiten Factura C
      return {
        invoiceType: 'C',
        requiresCuit: this.requiresCuitForReceptor(receptorType),
        requiresBusinessName: this.requiresBusinessNameForReceptor(receptorType),
        discriminatesVat: false,
        specialLegend: false
      }
    } else if (doctorType === 'responsable_inscripto') {
      // Responsable Inscripto puede emitir A o B
      if (receptorType === 'responsable_inscripto') {
        return {
          invoiceType: 'A',
          requiresCuit: true,
          requiresBusinessName: true,
          discriminatesVat: true,
          specialLegend: false
        }
      } else if (receptorType === 'monotributista') {
        return {
          invoiceType: 'A',
          requiresCuit: true,
          requiresBusinessName: false,
          discriminatesVat: true,
          specialLegend: true
        }
      } else if (receptorType === 'consumidor_final' || receptorType === 'exento_iva') {
        return {
          invoiceType: 'B',
          requiresCuit: receptorType === 'exento_iva',
          requiresBusinessName: receptorType === 'exento_iva',
          discriminatesVat: false,
          specialLegend: false
        }
      } else {
        // Receptor no reconocido
        return null
      }
    } else {
      // Tipo de doctor no reconocido
      return null
    }
  }

  requiresCuitForReceptor(receptorType) {
    return ['responsable_inscripto', 'monotributista', 'exento_iva'].includes(receptorType)
  }

  requiresBusinessNameForReceptor(receptorType) {
    return ['responsable_inscripto', 'exento_iva'].includes(receptorType)
  }

  validateCuitRequirements(invoiceType, receptorType) {
    const patientCuit = this.patientCuitTarget ? this.patientCuitTarget.textContent.trim() : ''
    const responsibleCuit = this.responsibleCuitTarget ? this.responsibleCuitTarget.textContent.trim() : ''

    const hasPatientCuit = patientCuit !== ''
    const hasResponsibleCuit = responsibleCuit !== ''
    const hasAnyCuit = hasPatientCuit || hasResponsibleCuit
    const normalizedInvoiceType = invoiceType.toLowerCase()


    // Replicar exactamente la lógica del backend
    if (normalizedInvoiceType === 'a' && !hasAnyCuit) {
      alert('Para generar Factura A, el paciente o su responsable debe tener un CUIT configurado')
      return false
    }

    if (normalizedInvoiceType === 'c' && receptorType !== 'consumidor_final' && !hasAnyCuit) {
      alert('Para generar Factura C, el paciente o su responsable debe tener un CUIT configurado')
      return false
    }

    // Factura B con receptor Exento IVA también requiere CUIT
    if (normalizedInvoiceType === 'b' && receptorType === 'exento_iva' && !hasAnyCuit) {
      alert('Para generar Factura B a Exento IVA, el paciente o su responsable debe tener un CUIT configurado')
      return false
    }

    return true
  }

  validateBusinessNameRequirements(invoiceType, receptorType) {
    // Solo validar razón social si es requerida
    if (!this.requiresBusinessNameForReceptor(receptorType)) {
      return true
    }

    // Obtener datos del paciente desde el DOM (necesitamos agregar estos campos)
    const patientCompanyName = document.querySelector('[data-patient-company-name]')?.textContent?.trim() || ''
    const responsibleCompanyName = document.querySelector('[data-responsible-company-name]')?.textContent?.trim() || ''

    const hasPatientCompanyName = patientCompanyName !== ''
    const hasResponsibleCompanyName = responsibleCompanyName !== ''
    const hasAnyCompanyName = hasPatientCompanyName || hasResponsibleCompanyName

    if (!hasAnyCompanyName) {
      alert('Para generar esta factura, el paciente o su responsable debe tener una razón social configurada')
      return false
    }

    return true
  }

  escapeHtml(str) {
    const div = document.createElement('div')
    div.textContent = str ?? ''
    return div.innerHTML
  }

  showInvoiceInfo(details) {
    if (!details) return

    const infoDiv = document.getElementById('invoice-info')
    const detailsSpan = document.getElementById('invoice-details')

    if (!infoDiv || !detailsSpan) return

    let message = `Se generará <strong>Factura ${details.invoiceType}</strong>`
    let requirements = []

    if (details.requiresCuit) {
      requirements.push('Requiere CUIT')
    }

    if (details.requiresBusinessName) {
      requirements.push('Requiere razón social')
    }

    if (details.discriminatesVat) {
      requirements.push('Discrimina IVA')
    } else {
      requirements.push('No discrimina IVA')
    }

    if (details.specialLegend) {
      requirements.push('Con leyenda especial para Monotributista')
    }

    if (requirements.length > 0) {
      message += '<ul class="mb-0 mt-2">'
      requirements.forEach(req => {
        message += `<li>${req}</li>`
      })
      message += '</ul>'
    }

    // Agregar previsualización de CUIT si es requerido
    if (details.requiresCuit) {
      const patientCuit = document.querySelector('[data-afip-invoice-target="patientCuit"]')?.textContent?.trim() || ''
      const responsibleCuit = document.querySelector('[data-afip-invoice-target="responsibleCuit"]')?.textContent?.trim() || ''

      if (patientCuit) {
        message += `<div class="mt-2"><small class="text-muted"><strong>CUIT del paciente:</strong> ${this.escapeHtml(patientCuit)}</small></div>`
      } else if (responsibleCuit) {
        message += `<div class="mt-2"><small class="text-muted"><strong>CUIT del responsable:</strong> ${this.escapeHtml(responsibleCuit)}</small></div>`
      } else {
        message += `<div class="mt-2"><small class="text-warning"><strong>⚠️ No se encontró CUIT configurado</strong></small></div>`
      }
    }

    // Agregar previsualización de razón social si es requerida
    if (details.requiresBusinessName) {
      const patientCompanyName = document.querySelector('[data-patient-company-name]')?.textContent?.trim() || ''
      const responsibleCompanyName = document.querySelector('[data-responsible-company-name]')?.textContent?.trim() || ''

      // Priorizar paciente primero, sino usar responsable
      if (patientCompanyName) {
        message += `<div class="mt-2"><small class="text-muted"><strong>Razón social del paciente:</strong> ${this.escapeHtml(patientCompanyName)}</small></div>`
      } else if (responsibleCompanyName) {
        message += `<div class="mt-2"><small class="text-muted"><strong>Razón social del responsable:</strong> ${this.escapeHtml(responsibleCompanyName)}</small></div>`
      } else {
        message += `<div class="mt-2"><small class="text-warning"><strong>⚠️ No se encontró razón social configurada</strong></small></div>`
      }
    }

    detailsSpan.innerHTML = message
    infoDiv.classList.remove('d-none')
  }

  hideInvoiceInfo() {
    const infoDiv = document.getElementById('invoice-info')
    if (infoDiv) {
      infoDiv.classList.add('d-none')
    }
  }

  generateInvoice() {
    const selectedReceptorType = this.hasReceptorTypeTarget ? this.receptorTypeTarget.value : null

    if (!selectedReceptorType) {
      alert('Debe seleccionar un tipo de receptor')
      return
    }

    const doctorType = this.doctorTypeValue
    const invoiceDetails = this.determineInvoiceDetails(doctorType, selectedReceptorType)

    if (!invoiceDetails) {
      alert('No se puede determinar el tipo de factura')
      return
    }

    // Validar CUIT según la lógica del backend
    if (!this.validateCuitRequirements(invoiceDetails.invoiceType, selectedReceptorType)) {
      return
    }

    // Validar razón social si es requerida
    if (!this.validateBusinessNameRequirements(invoiceDetails.invoiceType, selectedReceptorType)) {
      return
    }

    const confirmMessage = `¿Está seguro de que desea generar la Factura ${invoiceDetails.invoiceType}?`

    // Validar que tenga descripción de factura antes de proceder
    const baseDescriptionField = document.querySelector('textarea[name="appointment[base_description]"]')
    const diagnosisHealthInsuranceField = document.querySelector('textarea[name="appointment[diagnosis_health_insurance]"]')

    if (!baseDescriptionField || baseDescriptionField.value.trim() === '') {
      alert('Debe completar la descripción de la consulta antes de generar la factura AFIP.')
      if (baseDescriptionField) baseDescriptionField.focus()
      return
    }

    if (!diagnosisHealthInsuranceField || diagnosisHealthInsuranceField.value.trim() === '') {
      alert('Debe completar el diagnóstico y prepaga antes de generar la factura AFIP.')
      if (diagnosisHealthInsuranceField) diagnosisHealthInsuranceField.focus()
      return
    }

    if (confirm(confirmMessage)) {
      this.showSpinner()
      this.submitForm(invoiceDetails.invoiceType, selectedReceptorType)
    }
  }

  showSpinner() {
    // Obtener elementos del botón
    const button = this.buttonTarget

    const icon = button.querySelector('i')
    const originalText = button.textContent.trim()

    // Guardar el texto original para poder restaurarlo si es necesario
    button.dataset.originalText = originalText

    // Mostrar spinner y deshabilitar botón
    button.disabled = true
    button.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Generando factura...'
    button.style.cursor = 'not-allowed'
    button.style.opacity = '0.7'
  }

  submitForm(invoiceType, receptorType) {
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = `/appointments/${this.appointmentIdValue}/generate_afip_invoice`

    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    const csrfInput = document.createElement('input')
    csrfInput.type = 'hidden'
    csrfInput.name = 'authenticity_token'
    csrfInput.value = csrfToken
    form.appendChild(csrfInput)

    const typeInput = document.createElement('input')
    typeInput.type = 'hidden'
    typeInput.name = 'invoice_type'
    typeInput.value = invoiceType
    form.appendChild(typeInput)

    const receptorInput = document.createElement('input')
    receptorInput.type = 'hidden'
    receptorInput.name = 'receptor_type'
    receptorInput.value = receptorType
    form.appendChild(receptorInput)

    // Obtener el valor del campo de descripción de factura del formulario principal
    const descriptionField = document.querySelector('textarea[name="appointment[base_description]"]')
    if (descriptionField && descriptionField.value.trim() !== '') {
      const descriptionInput = document.createElement('input')
      descriptionInput.type = 'hidden'
      descriptionInput.name = 'appointment[base_description]'
      descriptionInput.id = 'appointment_base_description'
      descriptionInput.value = descriptionField.value.trim()
      form.appendChild(descriptionInput)
    }

    // Obtener el valor del campo de diagnóstico y prepaga del formulario principal
    const diagnosisHealthInsuranceField = document.querySelector('textarea[name="appointment[diagnosis_health_insurance]"]')
    if (diagnosisHealthInsuranceField && diagnosisHealthInsuranceField.value.trim() !== '') {
      const diagnosisHealthInsuranceInput = document.createElement('input')
      diagnosisHealthInsuranceInput.type = 'hidden'
      diagnosisHealthInsuranceInput.name = 'appointment[diagnosis_health_insurance]'
      diagnosisHealthInsuranceInput.id = 'appointment_diagnosis_health_insurance'
      diagnosisHealthInsuranceInput.value = diagnosisHealthInsuranceField.value.trim()
      form.appendChild(diagnosisHealthInsuranceInput)
    }

    document.body.appendChild(form)
    form.submit()
  }
}
